import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('shaheen_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// ─── Country list for autocomplete ───────────────────────────────────────────
const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Angola','Argentina','Armenia','Australia',
  'Austria','Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Bolivia',
  'Bosnia and Herzegovina','Brazil','Bulgaria','Cambodia','Cameroon','Canada',
  'Chile','China','Colombia','Croatia','Cuba','Cyprus','Czech Republic',
  'Denmark','Ecuador','Egypt','Estonia','Ethiopia','Finland','France','Georgia',
  'Germany','Ghana','Greece','Guatemala','Hungary','India','Indonesia','Iran',
  'Iraq','Ireland','Israel','Italy','Japan','Jordan','Kazakhstan','Kenya',
  'Kuwait','Latvia','Lebanon','Libya','Lithuania','Luxembourg','Malaysia',
  'Mexico','Moldova','Morocco','Mozambique','Myanmar','Netherlands','New Zealand',
  'Nigeria','North Korea','Norway','Oman','Pakistan','Palestine','Panama','Peru',
  'Philippines','Poland','Portugal','Qatar','Romania','Russia','Saudi Arabia',
  'Serbia','Singapore','Slovakia','Slovenia','Somalia','South Africa',
  'South Korea','Spain','Sri Lanka','Sudan','Sweden','Switzerland','Syria',
  'Taiwan','Thailand','Tunisia','Turkey','UAE','Uganda','Ukraine',
  'United Kingdom','United States','Uruguay','Uzbekistan','Venezuela','Vietnam',
  'Yemen','Zimbabwe',
];

// ─── Risk styling ─────────────────────────────────────────────────────────────
const RISK = {
  low:      { color: 'var(--green)',  bg: 'rgba(34,197,94,0.1)',   border: 'var(--green)',  label: 'LOW RISK' },
  medium:   { color: 'var(--amber)',  bg: 'rgba(245,158,11,0.1)',  border: 'var(--amber)',  label: 'MEDIUM RISK' },
  high:     { color: '#F97316',       bg: 'rgba(249,115,22,0.1)',  border: '#F97316',       label: 'HIGH RISK' },
  critical: { color: 'var(--red)',    bg: 'rgba(239,68,68,0.1)',   border: 'var(--red)',    label: 'CRITICAL RISK' },
};

const PRIORITY_COLOR = {
  critical: 'var(--red)',
  high:     '#F97316',
  medium:   'var(--amber)',
  low:      'var(--green)',
};

const s = {
  page: { padding: '20px 16px', overflowY: 'auto', flex: 1 },
  title: { fontSize: 18, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 },
  sub:   { fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 },
  card:  { background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px', marginBottom: 14 },
  label: { fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.07em', marginBottom: 6, display: 'block' },
  input: { width: '100%', fontSize: 13, padding: '10px 12px', background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', boxSizing: 'border-box' },
  genBtn: { width: '100%', padding: '12px', background: 'var(--gold)', color: 'var(--bg-base)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 12 },
  sectionHead: { fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.08em', marginBottom: 10, marginTop: 4 },
  threatRow: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: '0.5px solid var(--bg-raised)' },
  threatIcon: { fontSize: 20, flexShrink: 0, width: 28, textAlign: 'center', marginTop: 1 },
  checkRow: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 0', borderBottom: '0.5px solid var(--bg-raised)', cursor: 'pointer' },
  checkBox: { width: 18, height: 18, borderRadius: 5, border: '1.5px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1, background: 'var(--bg-raised)' },
  historyRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '0.5px solid var(--bg-raised)', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)', fontSize: 13 },
  printBtn: { fontSize: 11, padding: '6px 14px', background: 'transparent', border: '0.5px solid var(--border)', borderRadius: 6, color: 'var(--text-secondary)', cursor: 'pointer', marginLeft: 8 },
  vaultBtn: { fontSize: 11, padding: '6px 14px', background: 'var(--gold-dim)', border: '0.5px solid var(--gold)', borderRadius: 6, color: 'var(--gold)', cursor: 'pointer' },
};

// ─── Advisory display component ──────────────────────────────────────────────
function AdvisoryView({ advisory, onSaveVault, vaultSaving, vaultSaved }) {
  const [checked, setChecked] = useState({});
  const printRef = useRef(null);
  const risk = RISK[advisory.risk_level] || RISK.medium;

  const toggle = (id) => setChecked(p => ({ ...p, [id]: !p[id] }));

  const handlePrint = () => {
    const el = printRef.current;
    if (!el) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Travel Advisory — ${advisory.country}</title>
      <style>
        body { font-family: sans-serif; color: #111; padding: 32px; max-width: 700px; margin: 0 auto; }
        h1 { font-size: 22px; margin-bottom: 4px; }
        h2 { font-size: 14px; color: #666; margin: 20px 0 8px; text-transform: uppercase; letter-spacing: 0.06em; }
        p { font-size: 13px; line-height: 1.6; color: #333; }
        .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; background: #fef3c7; color: #92400e; margin-bottom: 12px; }
        .threat { display: flex; gap: 10px; margin-bottom: 8px; font-size: 13px; }
        .rec { display: flex; gap: 8px; margin-bottom: 6px; font-size: 13px; align-items: flex-start; }
        .dot { width: 6px; height: 6px; border-radius: 50%; background: #92400e; flex-shrink: 0; margin-top: 5px; }
        .footer { margin-top: 32px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 12px; }
      </style></head><body>
      <h1>${advisory.flag || '🌍'} ${advisory.country} — Travel Security Advisory</h1>
      ${advisory.travel_date ? `<p style="color:#666">Planned travel: ${advisory.travel_date}</p>` : ''}
      <div class="badge">${risk.label}</div>
      ${advisory.ncsi_score ? `<p>National Cyber Security Index: <strong>${advisory.ncsi_score}/100</strong>${advisory.ncsi_rank ? ` (Rank #${advisory.ncsi_rank})` : ''}</p>` : ''}
      <h2>Risk Overview</h2><p>${advisory.risk_summary || ''}</p>
      <h2>Top Threats</h2>
      ${(advisory.top_threats || []).map(t => `<div class="threat"><span>${t.icon}</span><div><strong>${t.name}</strong><br>${t.description}</div></div>`).join('')}
      <h2>Recommendations</h2>
      ${(advisory.recommendations || []).map(r => `<div class="rec"><div class="dot"></div><div>[${r.category}] ${r.text}</div></div>`).join('')}
      ${advisory.geopolitical_context ? `<h2>Geopolitical Context</h2><p>${advisory.geopolitical_context}</p>` : ''}
      <div class="footer">Generated by Shaheen on ${new Date(advisory.generated_at || advisory.created_at || Date.now()).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}</div>
      </body></html>`);
    w.document.close();
    w.print();
  };

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const totalRecs = (advisory.recommendations || []).length;

  return (
    <div ref={printRef}>
      {/* Risk banner */}
      <div style={{ borderRadius: 10, padding: '14px 16px', marginBottom: 14, background: risk.bg, border: `0.5px solid ${risk.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 28 }}>{advisory.flag || '🌍'}</span>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>{advisory.country}</div>
              {advisory.travel_date && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>Travel: {advisory.travel_date}</div>}
            </div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 12, background: risk.color + '22', border: `0.5px solid ${risk.color}`, color: risk.color, letterSpacing: '0.05em' }}>
            {risk.label}
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {advisory.risk_summary}
        </div>
      </div>

      {/* NCSI score */}
      {advisory.ncsi_score !== null && advisory.ncsi_score !== undefined && (
        <div style={{ ...s.card, display: 'flex', gap: 14, alignItems: 'center', padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: 26, fontWeight: 600, color: risk.color }}>{advisory.ncsi_score}</div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: '0.06em' }}>NCSI / 100</div>
          </div>
          <div style={{ flex: 1, borderLeft: '0.5px solid var(--border)', paddingLeft: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 3 }}>National Cyber Security Index</div>
            {advisory.ncsi_rank && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Ranked #{advisory.ncsi_rank} of {advisory.ncsi_total || 193} countries
              </div>
            )}
            <div style={{ marginTop: 6, height: 4, background: 'var(--bg-raised)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${advisory.ncsi_score}%`, background: risk.color, borderRadius: 4, transition: 'width 0.8s ease' }} />
            </div>
          </div>
        </div>
      )}

      {/* Border warning */}
      {advisory.border_warning && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '0.5px solid var(--red)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--red)', lineHeight: 1.5 }}>
          ⚠️ <strong>Border device inspection risk.</strong> Authorities in this country may compel access to your devices at the border. Consider using a dedicated travel device.
        </div>
      )}
      {advisory.burner_recommended && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '0.5px solid var(--red)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--red)', lineHeight: 1.5 }}>
          🔴 <strong>Burner device strongly recommended</strong> for this destination. Do not bring your primary phone or laptop.
        </div>
      )}

      {/* Top threats */}
      {(advisory.top_threats || []).length > 0 && (
        <div style={s.card}>
          <div style={s.sectionHead}>TOP THREATS</div>
          {advisory.top_threats.map((t, i) => (
            <div key={t.id || i} style={{ ...s.threatRow, ...(i === advisory.top_threats.length - 1 ? { borderBottom: 'none', paddingBottom: 0 } : {}) }}>
              <div style={s.threatIcon}>{t.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 3 }}>{t.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{t.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recommendations checklist */}
      {(advisory.recommendations || []).length > 0 && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={s.sectionHead}>SECURITY CHECKLIST</div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{checkedCount}/{totalRecs} done</span>
          </div>
          {/* Group by category */}
          {['Device', 'Network', 'Border', 'Communications', 'Access', 'Awareness', 'Return'].map(cat => {
            const items = (advisory.recommendations || []).filter(r => r.category === cat);
            if (!items.length) return null;
            return (
              <div key={cat} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4, paddingBottom: 4, borderBottom: '0.5px solid var(--bg-raised)' }}>{cat}</div>
                {items.map((r, i) => (
                  <div key={r.id || i} style={s.checkRow} onClick={() => toggle(r.id || `${cat}-${i}`)}>
                    <div style={{ ...s.checkBox, ...(checked[r.id || `${cat}-${i}`] ? { background: 'var(--green)', borderColor: 'var(--green)' } : {}) }}>
                      {checked[r.id || `${cat}-${i}`] && <span style={{ fontSize: 11, color: 'white', fontWeight: 700 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, textDecoration: checked[r.id || `${cat}-${i}`] ? 'line-through' : 'none', opacity: checked[r.id || `${cat}-${i}`] ? 0.5 : 1 }}>
                        {r.text}
                      </div>
                    </div>
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 6, color: PRIORITY_COLOR[r.priority] || 'var(--text-muted)', border: `0.5px solid ${PRIORITY_COLOR[r.priority] || 'var(--border)'}`, flexShrink: 0 }}>
                      {(r.priority || '').toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Geopolitical context */}
      {advisory.geopolitical_context && (
        <div style={{ ...s.card, borderLeft: `3px solid var(--gold)` }}>
          <div style={s.sectionHead}>GEOPOLITICAL CONTEXT</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{advisory.geopolitical_context}</div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 4, marginBottom: 20 }}>
        <button style={s.vaultBtn} onClick={onSaveVault} disabled={vaultSaving || vaultSaved}>
          {vaultSaved ? '✓ Saved to vault' : vaultSaving ? 'Saving…' : 'Save to vault'}
        </button>
        <button style={s.printBtn} onClick={handlePrint}>
          Print / PDF
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function TravelAdvisory() {
  const { client } = useAuth();
  const nav = useNavigate();

  const [country, setCountry] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [advisory, setAdvisory] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [vaultSaving, setVaultSaving] = useState(false);
  const [vaultSaved, setVaultSaved] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Load history
  useEffect(() => {
    if (!client?.id) return;
    authFetch(`${API}/client/${client.id}/travel-advisories`)
      .then(r => r.json())
      .then(d => setHistory(d.advisories || []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [client]);

  // Autocomplete
  const handleCountryInput = (val) => {
    setCountry(val);
    if (val.length < 2) { setSuggestions([]); return; }
    const lower = val.toLowerCase();
    setSuggestions(COUNTRIES.filter(c => c.toLowerCase().startsWith(lower)).slice(0, 6));
    setShowSuggestions(true);
  };

  const selectCountry = (c) => { setCountry(c); setSuggestions([]); setShowSuggestions(false); };

  const handleGenerate = async () => {
    if (!country.trim()) { setError('Please enter a destination country'); return; }
    setGenerating(true);
    setError('');
    setAdvisory(null);
    setVaultSaved(false);
    try {
      const r = await authFetch(`${API}/client/${client.id}/travel-advisory`, {
        method: 'POST',
        body: JSON.stringify({ country: country.trim(), travel_date: travelDate || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to generate advisory');
      setAdvisory(data);
      // Refresh history
      authFetch(`${API}/client/${client.id}/travel-advisories`)
        .then(r2 => r2.json())
        .then(d => setHistory(d.advisories || []))
        .catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const loadAdvisory = async (id) => {
    try {
      const r = await authFetch(`${API}/client/${client.id}/travel-advisories/${id}`);
      const data = await r.json();
      if (r.ok) {
        setAdvisory({ ...data, ...data.advisory_content });
        setVaultSaved(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {}
  };

  const handleSaveVault = async () => {
    if (!advisory || !client?.id) return;
    setVaultSaving(true);
    try {
      const token = localStorage.getItem('shaheen_token');
      await fetch(`${API}/client/${client.id}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          title: `Travel Advisory — ${advisory.country}`,
          document_type: 'travel_advisory',
          content: advisory,
        }),
      });
      setVaultSaved(true);
    } catch {} finally { setVaultSaving(false); }
  };

  const deleteAdvisory = async (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      const r = await authFetch(`${API}/client/${client.id}/travel-advisories/${id}`, { method: 'DELETE' });
      if (r.ok) {
        setHistory(prev => prev.filter(h => h.id !== id));
        if (advisory?.id === id) setAdvisory(null);
      }
    } catch {}
    setDeletingId(null);
  };

  const RISK_LEVELS = ['low', 'medium', 'high', 'critical'];

  return (
    <div style={s.page}>
      <div style={s.title}>Travel Security Advisory</div>
      <div style={s.sub}>Get a personalised cyber-security briefing before you travel</div>

      {/* Generator */}
      <div style={s.card}>
        <div style={s.sectionHead}>DESTINATION</div>
        <div style={{ position: 'relative' }}>
          <input
            style={s.input}
            placeholder="Country name (e.g. Russia, China, UAE…)"
            value={country}
            onChange={e => handleCountryInput(e.target.value)}
            onFocus={() => country.length >= 2 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            onKeyDown={e => { if (e.key === 'Enter') handleGenerate(); }}
            autoComplete="off"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 8, zIndex: 10, overflow: 'hidden', marginTop: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
              {suggestions.map(c => (
                <div key={c} style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', borderBottom: '0.5px solid var(--bg-raised)' }}
                  onMouseDown={() => selectCountry(c)}>{c}</div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={s.label}>TRAVEL DATE (optional)</label>
          <input
            style={s.input}
            type="date"
            value={travelDate}
            onChange={e => setTravelDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        {error && (
          <div style={{ fontSize: 12, color: 'var(--red)', background: 'rgba(239,68,68,0.08)', border: '0.5px solid var(--red)', borderRadius: 6, padding: '8px 12px', marginTop: 10 }}>
            {error}
          </div>
        )}

        <button style={{ ...s.genBtn, opacity: generating ? 0.7 : 1 }} onClick={handleGenerate} disabled={generating}>
          {generating ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(10,10,10,0.3)', borderTopColor: '#0a0a0a', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Preparing your travel briefing...
            </span>
          ) : '✈  Request travel briefing'}
        </button>
      </div>

      {/* Advisory result */}
      {advisory && (
        <AdvisoryView
          advisory={advisory}
          onSaveVault={handleSaveVault}
          vaultSaving={vaultSaving}
          vaultSaved={vaultSaved}
        />
      )}

      {/* Past advisories */}
      <div style={s.card}>
        <div style={s.sectionHead}>PAST ADVISORIES</div>
        {historyLoading ? (
          <div style={s.empty}>One moment...</div>
        ) : history.length === 0 ? (
          <div style={s.empty}>No advisories on record yet. Enter a destination above to generate your first one.</div>
        ) : (
          history.map((h, i) => {
            const risk = RISK[h.risk_level] || RISK.medium;
            const flag = h.country_code && h.country_code.length === 2
              ? String.fromCodePoint(...[...h.country_code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65))
              : '🌍';
            return (
              <div key={h.id} style={{ ...s.historyRow, ...(i === history.length - 1 ? { borderBottom: 'none' } : {}) }}
                onClick={() => loadAdvisory(h.id)}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{h.country}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {h.travel_date ? `Travel: ${h.travel_date} · ` : ''}
                    {new Date(h.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: risk.bg, border: `0.5px solid ${risk.border}`, color: risk.color }}>
                    {risk.label}
                  </span>
                  {h.ncsi_score && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', marginTop: 3 }}>NCSI {h.ncsi_score}</div>
                  )}
                </div>
                <button
                  onClick={(e) => deleteAdvisory(e, h.id)}
                  disabled={deletingId === h.id}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted)', opacity: deletingId === h.id ? 0.4 : 0.6, flexShrink: 0 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                </button>
                <span style={{ fontSize: 18, color: 'var(--text-muted)' }}>›</span>
              </div>
            );
          })
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
